import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderBar from "../../../components/Header/HeaderBar";
import PropertyList from "../../../components/Lists/PropertyList";
import { CurrentUser } from "../../../store/storeTypes";
import axios from "../../../services/axios/backendAxios";
import flash from "../../../services/flash";
import { confirmWrapper } from "../../../components/Modals/ConfirmWrapper";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { auth, Job } from "@mysnowscape/snow-lib";
import ModalWrapper from "../../../components/Modals/ModalWrapper";
import { Company } from "../../../types";
import Select from "react-select";
import SubtleButton from "../../../components/Buttons/SubtleButton";
import errors from "../../../services/errors";
import { pickSeason } from "../../../components/Modals/PickSeason";
import { useFeatureFlag, User } from "configcat-react";
import { CurrentUserMaybeId } from "./EditJobPage";
import {
  buildPropertyListQueryParams,
  getPropertyListFiltersFromQuery,
  getPropertyListSearchTerm,
} from "../../../utils/propertyListFilters";

const uidWithCopyMode = [
  "JFxZlK9vLNQaW0AS3z3EfxFKyFp2",
  "CuG79MaAodeMdYmRqmveiK2UPSF3",
];

function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const AdminJobsPage = ({
  currentUser,
}: {
  currentUser: CurrentUserMaybeId;
}) => {
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  // The url is the source of truth for the filters, so back / forward restore
  // the same view the same way the property history page does.
  const currentFilter = getPropertyListFiltersFromQuery(queryParams);
  const searchTerm = getPropertyListSearchTerm(queryParams);
  const [selectedJobs, setSelectedJobs] = useState<Job[]>([]);
  const queryClient = useQueryClient();
  const [showSelectCompany, setShowSelectCompany] = useState(false);
  const previousCompanyId = useRef(currentUser.currentCompanyId);
  let { value: showSharePropertyButtons } = useFeatureFlag(
    "showSharePropertyButtons",
    false,
    currentUser._id
      ? new User(currentUser._id, undefined, undefined, {
          companyId: currentUser.currentCompanyId ?? "unknown",
        })
      : undefined,
  );

  const updateFiltersInUrl = (
    newFilters: Record<string, unknown>,
    newSearchTerm: string | null = searchTerm,
  ) => {
    const queryString = buildPropertyListQueryParams(newFilters, newSearchTerm);
    navigate(
      queryString ? `/admin/property?${queryString}` : "/admin/property",
    );
  };

  // If we change companies, selectedJobs should reset and the previous
  // company's filters should not carry over.
  useEffect(() => {
    setSelectedJobs([]);
    if (previousCompanyId.current !== currentUser.currentCompanyId) {
      previousCompanyId.current = currentUser.currentCompanyId;
      navigate("/admin/property");
    }
  }, [currentUser.currentCompanyId, navigate]);

  // Mutation to support the copy button action
  const copyCompaniesMutation = useMutation({
    mutationFn: (companyId: string) => {
      return axios.post("job/copy-to", {
        source: {
          companyId: currentUser.currentCompanyId,
          properties: selectedJobs.map((j) => j._id),
        },
        destination: {
          companyId,
        },
      });
    },
    onSuccess: () => {
      flash.success(
        "In Progress. Please wait up to 20 minutes for the properties to copy.",
      );
    },
    onError: (err) => {
      errors.report(err).then((errorId) => {
        flash.error(`Error copying properties. ErrorId: ${errorId}`);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["jobs"],
      });
    },
  });

  return (
    <div className="flex flex-col items-center">
      <HeaderBar
        buttons={[
          ...(auth.canSuperAdmin(currentUser) ||
          uidWithCopyMode.includes(currentUser.uid)
            ? [
                {
                  disabled: selectedJobs.length < 1,
                  loading: copyCompaniesMutation.isPending,
                  label: `Copy${selectedJobs.length > 0 ? " " + selectedJobs.length : ""}`,
                  onClick: async () => {
                    setShowSelectCompany(true);
                  },
                },
              ]
            : []),
          ...(showSharePropertyButtons
            ? [
                {
                  disabled: selectedJobs.length < 1,
                  label: "Share",
                  onClick: async () => {
                    navigate(
                      `/admin/property/share?jobIds=${encodeURIComponent(selectedJobs.map((j) => j._id).join(","))}`,
                    );
                  },
                },
              ]
            : []),
          {
            color: "red",
            disabled: selectedJobs.length < 1,
            label: `Delete${selectedJobs.length > 0 ? " " + selectedJobs.length : ""}`,
            onClick: async () => {
              const shouldContinue = await confirmWrapper(
                `Delete ${selectedJobs.length} properties?`,
                {
                  okText: "Delete",
                  description:
                    "This will delete these jobs and remove them from all routes.",
                },
              );
              if (shouldContinue) {
                const promises: Promise<{
                  jobName: string;
                  success: boolean;
                }>[] = [];
                selectedJobs.map((s) => {
                  promises.push(
                    axios
                      .delete(`job/${s._id}/delete`)
                      .then(() => ({ jobName: s.name, success: true }))
                      .catch(() => ({ jobName: s.name, success: false })),
                  );
                });
                const results = await Promise.all(promises);
                const errors = results.filter((r) => !r.success);
                if (errors.length > 0) {
                  flash.error(
                    "Some jobs failed to delete: " +
                      errors.map((e) => e.jobName).join(", "),
                  );
                } else {
                  flash.success("All marked jobs deleted");
                }
                queryClient.invalidateQueries({
                  queryKey: ["jobs"],
                });
                setSelectedJobs([]);
              }
            },
          },
          {
            disabled: selectedJobs.length < 2,
            label: `Bulk Edit`,
            onClick: async () => {
              const season = await pickSeason();
              navigate(
                `/admin/property/bulk-edit?jobIds=${encodeURIComponent(selectedJobs.map((j) => j._id).join(","))}&season=${encodeURIComponent(season)}`,
              );
            },
          },
          {
            color: "blue",
            label: "Bulk Import",
            onClick: () => navigate("/admin/excel_import"),
          },
          {
            color: "blue",
            label: "Create Property",
            onClick: () => navigate("create"),
          },
        ]}
        title="Properties"
      />
      <PropertyList
        currentUser={currentUser}
        onJobClick={(jobId) => navigate("/admin/property/" + jobId)}
        selectedJobs={selectedJobs}
        showInactiveFilter={true}
        filters={currentFilter}
        searchTerm={searchTerm}
        onFiltersChange={(newFilters) => {
          setSelectedJobs([]);
          updateFiltersInUrl(newFilters);
        }}
        onSearchChange={(newSearchTerm) => {
          updateFiltersInUrl(currentFilter, newSearchTerm);
        }}
        onJobSelected={(j, checked) =>
          setSelectedJobs((current) => {
            console.log(j, checked);
            if (checked) {
              return [...current, j];
            } else {
              return [...current.filter((c) => c._id !== j._id)];
            }
          })
        }
      />
      {showSelectCompany && (
        <SelectCompanyModal
          cancel={() => setShowSelectCompany(false)}
          onFinish={(companyId) => {
            setShowSelectCompany(false);
            copyCompaniesMutation.mutate(companyId);
          }}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(AdminJobsPage);
export { AdminJobsPage };

function SelectCompanyModal({
  cancel,
  onFinish,
  currentUser,
}: {
  cancel: () => void;
  onFinish: (companyId: string) => void;
  currentUser: CurrentUser;
}) {
  const companyQuery = useQuery<{
    userCompanies: Array<Company & { isPaid: boolean }>;
  }>({
    queryKey: [`user/${currentUser.uid}/companies`],
    enabled: () => currentUser.uid != null,
  });
  const filtered = companyQuery.data?.userCompanies
    ?.filter(
      (c) =>
        auth.canAdminCompany(currentUser, c._id) &&
        c.isPaid &&
        c._id != currentUser.currentCompanyId,
    )
    .map((c) => ({
      label: c.name,
      value: c._id,
    }));
  return (
    <ModalWrapper clickOff={cancel}>
      <div className="bg-white p-5 z-10 rounded-xl flex-col items-center max-w-[600px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const form = e.currentTarget as HTMLFormElement;
            const formData = new FormData(form);
            const company_id = formData.get("company_id")!.toString();
            onFinish(company_id);
          }}
        >
          <h2 className="text-center font-bold">
            Choose the Destination Company
          </h2>
          <Select
            isLoading={companyQuery.isPending}
            name="company_id"
            options={filtered}
            placeholder="Select Company"
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderColor: "#829399",
              }),
            }}
          />
          <p className="text-orange">
            WARNING: this is an experimental feature and might not work
            properly. If the property has been copied to this company before, it
            will be overwritten. The copy will take about 30 seconds per 100
            properties so please be patient.
          </p>
          <SubtleButton
            onClick={undefined}
            disabled={undefined}
            loading={undefined}
            loaderColor={undefined}
            loaderSize={undefined}
            label={undefined}
            className={undefined}
            testId={undefined}
            title={undefined}
            type="submit"
            buttonRole="primary"
          >
            Start Copy
          </SubtleButton>
        </form>
      </div>
    </ModalWrapper>
  );
}
